import { motion } from "framer-motion";
import { Truck, Store, MapPin, ArrowLeft, Loader2, Lock, ShieldCheck } from "lucide-react";
import { formatCAD } from "@/lib/api";
import { SquareCard } from "@/components/SquareCard";

export const field =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-all focus:border-leaf focus:ring-4 focus:ring-leaf/15 placeholder:text-muted-foreground";

const PROVINCES = ["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE", "NT", "YT", "NU"];

const stepMotion = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

export function InformationStep({ method, setMethod, info, setInfo, pickupSlot, setPickupSlot, pickupSlots, onContinue }) {
  return (
    <motion.div key="info" {...stepMotion} className="space-y-6">
      <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
        <h2 className="font-display text-3xl font-semibold text-forest">Delivery method</h2>
        <p className="mt-1 text-sm text-muted-foreground">Choose how you'd like to receive your order.</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            data-testid="method-ship"
            onClick={() => setMethod("ship")}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${method === "ship" ? "border-forest bg-leaf/10 ring-1 ring-forest" : "border-border hover:border-clay/40"}`}
          >
            <Truck className={`mt-0.5 h-5 w-5 shrink-0 ${method === "ship" ? "text-forest" : "text-muted-foreground"}`} />
            <span>
              <span className="block font-semibold text-forest">Delivery to my address</span>
              <span className="block text-xs text-muted-foreground">By our in-house drivers · fee based on distance</span>
            </span>
          </button>
          <button
            type="button"
            data-testid="method-pickup"
            onClick={() => setMethod("pickup")}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${method === "pickup" ? "border-forest bg-leaf/10 ring-1 ring-forest" : "border-border hover:border-clay/40"}`}
          >
            <Store className={`mt-0.5 h-5 w-5 shrink-0 ${method === "pickup" ? "text-forest" : "text-muted-foreground"}`} />
            <span>
              <span className="block font-semibold text-forest">Pick up in-store <span className="text-clay">· Free</span></span>
              <span className="block text-xs text-muted-foreground">22 Midlake Blvd SE #518, Calgary</span>
            </span>
          </button>
        </div>
      </div>

      <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
        <h2 className="font-display text-3xl font-semibold text-forest">Contact</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <input data-testid="checkout-name" className={field} placeholder="Full name" value={info.customer_name} onChange={(e) => setInfo({ ...info, customer_name: e.target.value })} />
          <input data-testid="checkout-email" className={field} placeholder="Email address" value={info.customer_email} onChange={(e) => setInfo({ ...info, customer_email: e.target.value })} />
          <input data-testid="checkout-phone" className={`${field} sm:col-span-2`} placeholder="Phone (optional)" value={info.customer_phone} onChange={(e) => setInfo({ ...info, customer_phone: e.target.value })} />
        </div>
        {method === "pickup" && (
          <p data-testid="pickup-phone-hint" className="mt-2 text-xs text-muted-foreground">Add your phone number and we'll text you an SMS when your order is ready for pickup.</p>
        )}
      </div>

      {method === "ship" ? (
        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
          <h2 className="font-display text-3xl font-semibold text-forest">Shipping address</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <input data-testid="checkout-line1" className={`${field} sm:col-span-2`} placeholder="Street address" value={info.line1} onChange={(e) => setInfo({ ...info, line1: e.target.value })} />
            <input data-testid="checkout-line2" className={`${field} sm:col-span-2`} placeholder="Apartment, suite (optional)" value={info.line2} onChange={(e) => setInfo({ ...info, line2: e.target.value })} />
            <input data-testid="checkout-city" className={field} placeholder="City" value={info.city} onChange={(e) => setInfo({ ...info, city: e.target.value })} />
            <select data-testid="checkout-country" className={field} value={info.country} onChange={(e) => setInfo({ ...info, country: e.target.value })}>
              <option value="CA">Canada</option>
              <option value="US">United States</option>
              <option value="OTHER">Other</option>
            </select>
            {info.country === "CA" ? (
              <select data-testid="checkout-province" className={field} value={info.province} onChange={(e) => setInfo({ ...info, province: e.target.value })}>
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            ) : (
              <input data-testid="checkout-province" className={field} placeholder="State / Region" value={info.province} onChange={(e) => setInfo({ ...info, province: e.target.value })} />
            )}
            <input data-testid="checkout-postal" className={field} placeholder="Postal code" value={info.postal_code} onChange={(e) => setInfo({ ...info, postal_code: e.target.value })} />
          </div>
        </div>
      ) : (
        <div data-testid="pickup-info-card" className="rounded-2xl border border-forest/20 bg-forest/5 p-6">
          <div className="flex items-start gap-3">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-clay" />
            <div>
              <h2 className="font-display text-2xl font-semibold text-forest">Pickup location</h2>
              <p className="mt-1 text-sm font-medium text-forest">Bambam African Mart</p>
              <p className="text-sm text-muted-foreground">22 Midlake Blvd SE #518, Calgary, AB</p>
              <p className="mt-3 text-sm text-muted-foreground">No shipping address needed — we'll email or text you the moment your order is ready to collect.</p>
            </div>
          </div>
          <div className="mt-5 border-t border-forest/10 pt-5">
            <label className="text-sm font-medium text-forest">Preferred pickup time</label>
            <select data-testid="pickup-slot-select" className={`${field} mt-2`} value={pickupSlot} onChange={(e) => setPickupSlot(e.target.value)}>
              <option value="">As soon as it's ready</option>
              {pickupSlots.map((s) => (
                <option key={s.value} value={s.value}>{s.value}</option>
              ))}
            </select>
            <p className="mt-2 text-xs text-muted-foreground">Choose a window that works for you — we'll have it ready and let you know.</p>
          </div>
        </div>
      )}

      <button data-testid="to-shipping-btn" onClick={onContinue} className="w-full rounded-full bg-clay py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A3161F]">
        {method === "pickup" ? "Continue to payment" : "Continue to shipping"}
      </button>
    </motion.div>
  );
}

export function ShippingStep({ quoteInfo, info, loadingRates, deliveryError, rates, rateId, setRateId, onSwitchToPickup, onBack, onContinue }) {
  return (
    <motion.div key="ship" {...stepMotion} className="space-y-6">
      <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
        <h2 className="font-display text-3xl font-semibold text-forest">Delivery options</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {quoteInfo
            ? `${quoteInfo.distance_km} km from our store · ${quoteInfo.rush ? "rush-hour pricing in effect" : "off-peak pricing"}`
            : `In-house courier to ${info.city || "your area"}, ${info.province}`}
        </p>
        <div className="mt-5 space-y-3">
          {loadingRates ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground" data-testid="delivery-loading"><Loader2 className="h-5 w-5 animate-spin" /> Calculating your delivery fee…</div>
          ) : deliveryError ? (
            <div data-testid="delivery-unavailable" className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
              <p className="text-sm text-destructive">{deliveryError}</p>
              <button data-testid="switch-to-pickup" onClick={onSwitchToPickup} className="mt-3 inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-white hover:bg-forest/90">
                <Store className="h-4 w-4" /> Switch to in-store pickup
              </button>
            </div>
          ) : (
            rates.map((r) => (
              <label key={r.id} data-testid={`rate-${r.id}`} className={`flex cursor-pointer items-center justify-between rounded-xl border p-4 transition-colors ${rateId === r.id ? "border-forest bg-leaf/10 ring-1 ring-forest" : "border-border hover:border-clay/40"}`}>
                <div className="flex items-center gap-3">
                  <input type="radio" name="rate" checked={rateId === r.id} onChange={() => setRateId(r.id)} className="accent-[#0F4A23]" />
                  <div>
                    <p className="font-semibold text-forest">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.carrier} · {r.eta}</p>
                  </div>
                </div>
                <span className="font-bold text-forest">{r.amount_cents === 0 ? "Free" : formatCAD(r.amount_cents)}</span>
              </label>
            ))
          )}
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold text-forest hover:bg-forest/5">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button data-testid="to-payment-btn" onClick={onContinue} disabled={!rateId} className="flex-1 rounded-full bg-clay py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A3161F] disabled:opacity-50">
          Continue to payment
        </button>
      </div>
    </motion.div>
  );
}

export function PaymentStep({ squareCfg, squareEnabled, squareRef, card, setCard, placing, totalCents, onBack, onPlaceOrder }) {
  return (
    <motion.div key="pay" {...stepMotion} className="space-y-6">
      <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-3xl font-semibold text-forest">Payment</h2>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-forest/70"><Lock className="h-3.5 w-3.5" /> Secured by Square</span>
        </div>

        {!squareCfg.configured && (
          <div data-testid="mock-payment-notice" className="mt-4 rounded-xl border border-ochre/40 bg-ochre/10 px-4 py-3 text-sm text-forest">
            <strong>Demo mode:</strong> Square Sandbox credentials aren't connected yet, so this payment is simulated. Add your Square keys to take real card payments — no code changes needed.
          </div>
        )}

        {squareEnabled ? (
          <div className="mt-5">
            <p className="mb-3 text-xs text-muted-foreground">Enter your card details below. Payments are securely processed by Square.</p>
            <SquareCard ref={squareRef} />
          </div>
        ) : (
          <div className="mt-5 grid gap-4">
            <input data-testid="card-name" className={field} placeholder="Name on card" value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} />
            <input data-testid="card-number" className={field} placeholder="Card number" inputMode="numeric" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value.replace(/[^0-9 ]/g, "").slice(0, 19) })} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="card-exp" className={field} placeholder="MM / YY" value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value.slice(0, 7) })} />
              <input data-testid="card-cvc" className={field} placeholder="CVC" inputMode="numeric" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })} />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold text-forest hover:bg-forest/5">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button data-testid="place-order-btn" onClick={onPlaceOrder} disabled={placing} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-forest py-3.5 text-sm font-semibold text-sand transition-colors hover:bg-[#0A3318] disabled:opacity-60">
          {placing ? <><Loader2 className="h-4 w-4 animate-spin" /> Placing order…</> : <>Pay {formatCAD(totalCents)}</>}
        </button>
      </div>
    </motion.div>
  );
}

export function OrderSummary({ items, subtotalCents, discount, discountCents, selectedRate, shippingCents, taxCents, totalCents, promo, setPromo, applyPromo, applyingPromo, onRemovePromo, customerEmail }) {
  return (
    <div className="sticky top-24 rounded-[1.5rem] border border-border/70 bg-white p-6">
      <h2 className="font-display text-2xl font-semibold text-forest">Order summary</h2>
      <div className="mt-4 max-h-56 space-y-3 overflow-y-auto px-2 pt-2">
        {items.map((i) => (
          <div key={i.product_id} className="flex items-center gap-3">
            <div className="relative shrink-0">
              <img src={i.image_url} alt={i.name} className="h-14 w-14 rounded-lg object-cover" />
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-forest px-1 text-[11px] font-bold leading-none text-sand ring-2 ring-card">{i.quantity}</span>
            </div>
            <div className="flex-1 leading-tight">
              <p className="text-sm font-medium text-forest">{i.name}</p>
              <p className="text-xs text-muted-foreground">{i.unit}</p>
            </div>
            <span className="text-sm font-semibold text-forest">{formatCAD(i.price_cents * i.quantity)}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 space-y-2 border-t border-border pt-4 text-sm">
        <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-medium text-forest">{formatCAD(subtotalCents)}</span></div>
        {discount && (
          <div className="flex justify-between text-clay"><span>Discount ({discount.code})</span><span className="font-medium" data-testid="summary-discount">−{formatCAD(discountCents)}</span></div>
        )}
        <div className="flex justify-between text-muted-foreground"><span>Delivery</span><span className="font-medium text-forest">{selectedRate ? (shippingCents === 0 ? "Free" : formatCAD(shippingCents)) : "—"}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>Tax (GST 5%)</span><span className="font-medium text-forest">{formatCAD(taxCents)}</span></div>
        <div className="flex justify-between border-t border-border pt-3 text-base font-bold text-forest"><span>Total</span><span data-testid="order-total">{formatCAD(totalCents)}</span></div>
      </div>

      <div className="mt-4">
        {discount ? (
          <div className="flex items-center justify-between rounded-xl border border-clay/40 bg-clay/5 px-3 py-2 text-sm">
            <span className="font-medium text-clay">{discount.code} applied</span>
            <button data-testid="remove-promo" onClick={onRemovePromo} className="text-xs text-muted-foreground hover:text-destructive">Remove</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Promo code" className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm uppercase outline-none focus:border-clay" />
            <button data-testid="apply-promo" onClick={applyPromo} disabled={applyingPromo} className="rounded-xl bg-forest px-4 py-2 text-sm font-semibold text-sand hover:bg-[#0A3318] disabled:opacity-60">
              Apply
            </button>
          </div>
        )}
      </div>
      {selectedRate?.id === "pickup" && (
        <div data-testid="pickup-notice" className="mt-4 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-forest">
          <span className="font-semibold">In-store pickup:</span> We'll email you at <span className="font-medium">{customerEmail || "your email"}</span> as soon as your order is ready to collect at 22 Midlake Blvd SE #518, Calgary.
        </div>
      )}
      <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-clay" /> Encrypted & secure checkout</p>
    </div>
  );
}
